import {
  Button,
  type ColumnDef,
  DocumentIcon,
  EmptyState,
  Inline,
  PageContent,
  RecordPage,
  type RecordDetailSection,
  RelatedList,
  Stack,
  StatusLabel,
  TextLink,
} from '@olytic/foundation';
import type { CatalogEntry, GlossaryTerm } from '@olytic/foundation/catalog';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { authenticatedFetch } from '../auth/authClient';
import { useDocumentTitle } from '../documentTitle';
import {
  browseHref,
  entryHref,
  library,
  LIBRARY_ROUTE,
  shelfSingular,
  showcaseHref,
  STATUS_LABEL,
  STATUS_TONE,
} from './libraryData';

const FOUNDATION_REPO = 'https://github.com/Olytic/olytic-foundation/blob/main';

const ENTRY_COLUMNS: ColumnDef<CatalogEntry>[] = [
  { key: 'name', label: 'Name', primary: true, width: 220, get: (e) => e.name, href: (e) => entryHref(e) },
  { key: 'purpose', label: 'What It Is For', get: (e) => e.purpose },
];

const TERM_COLUMNS: ColumnDef<GlossaryTerm>[] = [
  { key: 'term', label: 'Term', width: 200, get: (t) => t.term },
  { key: 'definition', label: 'Meaning', get: (t) => t.definition },
];

function RouteLink({ to, children }: { to: string; children: string }): JSX.Element {
  return <TextLink asChild><Link to={to}>{children}</Link></TextLink>;
}

/** One building block: what it is for, when to use it, and what it is made of and used in. */
export function LibraryEntryPage(): JSX.Element {
  const { id = '' } = useParams();
  const entry = library.findEntry(decodeURIComponent(id));
  useDocumentTitle(entry ? `${entry.name} · Library` : 'Library');
  const showcase = entry ? showcaseHref(entry) : null;

  // The live demos are static files behind a sign-in cookie (static.mjs); ask
  // for it while the page is open so the demo link opens signed in.
  useEffect(() => {
    if (!showcase) return;
    authenticatedFetch('/api/library-session', { method: 'POST' }).catch(() => {
      // The demo then shows its own "sign in to Mothership" page.
    });
  }, [showcase]);

  if (!entry) {
    return (
      <PageContent as="section" width="fill">
        <EmptyState
          icon={<DocumentIcon />}
          title="That building block isn't in the Library"
          description="It may have been renamed. Search the Library for it."
          action={<Button asChild variant="outline"><Link to={LIBRARY_ROUTE}>Open the Library</Link></Button>}
        />
      </PageContent>
    );
  }

  return <EntryRecord entry={entry} showcase={showcase} />;
}

function EntryRecord({ entry, showcase }: { entry: CatalogEntry; showcase: string | null }): JSX.Element {
  const navigate = useNavigate();
  const shelf = library.shelfOf(entry.shelf);
  const builtFrom = library.entriesFor(entry.builtFrom);
  const usedIn = library.entriesFor(entry.usedIn);
  const terms = entry.terms.flatMap((t) => library.termOf(t) ?? []);

  const detailSections: RecordDetailSection[] = [
    {
      key: 'about',
      label: 'About',
      fields: [
        { key: 'use', label: 'When to Use It', value: entry.use, wide: true },
        { key: 'avoid', label: 'When Not to Use It', value: entry.avoid, wide: true },
        { key: 'aliases', label: 'Also Called', value: entry.aliases.length ? entry.aliases.join(', ') : null },
        {
          key: 'domains',
          label: 'Areas',
          value: entry.domains.length ? (
            <Inline as="span" gap="sm">
              {entry.domains.map((d) => (
                <RouteLink key={d} to={browseHref({ shelf: 'all', domain: d })}>{library.domainOf(d)?.name ?? d}</RouteLink>
              ))}
            </Inline>
          ) : 'Any app',
        },
        ...(entry.kit ? [{ key: 'kit', label: 'Kit', value: entry.kit }] : []),
      ],
    },
    ...(entry.options.length
      ? [{
        key: 'options',
        label: 'Options',
        fields: entry.options.map((o, i) => ({ key: `option-${i}`, label: o.name, value: o.summary, wide: true })),
      }]
      : []),
    {
      key: 'developers',
      label: 'For Developers',
      collapsed: true,
      fields: [
        { key: 'import', label: 'Import From', value: entry.import },
        { key: 'exports', label: 'Exports', value: entry.exports.length ? entry.exports.join(', ') : null },
        {
          key: 'playbook',
          label: 'Layout Playbook',
          value: entry.playbook ? (
            <TextLink href={`${FOUNDATION_REPO}/${entry.playbook}`} target="_blank" rel="noreferrer">
              {entry.playbook.split('/').pop()}
            </TextLink>
          ) : null,
        },
      ],
    },
  ];

  return (
    <PageContent as="section" width="fill">
      <RecordPage
        objectLabel={shelfSingular(entry.shelf)}
        objectIcon={<DocumentIcon />}
        title={entry.name}
        subtitle={entry.purpose}
        actions={
          showcase ? (
            <Button asChild variant="outline">
              <a href={showcase} target="_blank" rel="noreferrer">See It Live</a>
            </Button>
          ) : undefined
        }
        highlights={[
          { label: 'Code', value: entry.code },
          {
            label: 'Shelf',
            value: shelf ? <RouteLink to={browseHref({ shelf: entry.shelf })}>{shelf.name}</RouteLink> : entry.shelf,
          },
          { label: 'Status', value: <StatusLabel tone={STATUS_TONE[entry.status]}>{STATUS_LABEL[entry.status]}</StatusLabel> },
          { label: 'Added In', value: `Foundation ${entry.since}` },
        ]}
        detailSections={detailSections}
        tabs={[
          {
            key: 'related',
            label: 'Related',
            content: (
              <Stack gap="md">
                <RelatedList
                  title="Built From"
                  icon={<DocumentIcon />}
                  columns={ENTRY_COLUMNS}
                  rows={builtFrom}
                  getRowId={(e) => e.id}
                  previewCount={builtFrom.length}
                  onNavigate={navigate}
                  note={builtFrom.length ? `The building blocks ${entry.name} is assembled from.` : undefined}
                  emptyLabel="Not assembled from other building blocks."
                  compactEmpty
                />
                <RelatedList
                  title="Used In"
                  icon={<DocumentIcon />}
                  columns={ENTRY_COLUMNS}
                  rows={usedIn}
                  getRowId={(e) => e.id}
                  previewCount={usedIn.length}
                  onNavigate={navigate}
                  note={usedIn.length ? `The building blocks that include ${entry.name}.` : undefined}
                  emptyLabel="No other building block includes it."
                  compactEmpty
                />
                {terms.length ? (
                  <RelatedList
                    title="Glossary Terms"
                    icon={<DocumentIcon />}
                    columns={TERM_COLUMNS}
                    rows={terms}
                    getRowId={(t) => t.id}
                    previewCount={terms.length}
                  />
                ) : null}
              </Stack>
            ),
          },
        ]}
      />
    </PageContent>
  );
}
